'use client'

import { memo } from 'react'
import Link from 'next/link'
import { Github } from 'lucide-react'
import { FloatingIconsHero } from '@/components/ui/floating-icons-hero-section'
import { EIGHTBIT_CONNECT_PLATFORM_ICONS } from '@/app/(marketing)/_components/connect-platform-icons-8bit'
import {
  NIN_CONNECT_PLATFORM_URL,
  NIN_GITHUB_URL,
  NIN_MCP_SERVER_URL,
} from '@/app/nin/_components/nin-org-links'

type NinConnectPlatformVariant = 'org' | 'product' | 'enterprise' | 'govts' | 'banks'

type NinConnectPlatformSectionProps = {
  variant?: NinConnectPlatformVariant
}

const CONNECT_PLATFORM_COPY: Record<
  NinConnectPlatformVariant,
  { heading: string; subtitle: string }
> = {
  org: {
    heading: 'Plugins that connect your stack.',
    subtitle:
      'Open-source plugins you can self-host. Connect Gmail, finance, dev tools, and MCP servers to NINX or Ningenie: on your terms.',
  },
  product: {
    heading: 'Plugins that connect your stack.',
    subtitle:
      'Self-host the open-source Connect platform and plug Gmail, brokers, shell, and MCP into NINX: with your approval on every connection.',
  },
  enterprise: {
    heading: 'Integrate your internal stack securely.',
    subtitle:
      'Self-host Connect behind your firewall. Wire email, CRM, ERP, shell, and MCP into NINX Enterprise: with RBAC, audit trails, and IT approval on every connection.',
  },
  govts: {
    heading: 'Sovereign integrations for public service.',
    subtitle:
      'Deploy Connect on ministry-controlled infrastructure. Connect citizen systems, case files, and departmental tools to NINX Govts: no data crosses borders without policy approval.',
  },
  banks: {
    heading: 'Core-connected integrations for regulated finance.',
    subtitle:
      'Run Connect inside your banking perimeter. Wire core ledger, payments, CRM, and MCP tools to NINX Banks: with fraud gates, PCI boundaries, and auditor-ready connection logs.',
  },
}

export const NinConnectPlatformSection = memo(function NinConnectPlatformSection({
  variant = 'org',
}: NinConnectPlatformSectionProps) {
  const copy = CONNECT_PLATFORM_COPY[variant]

  return (
    <FloatingIconsHero
      aria-labelledby="nin-connect-platform-heading"
      icons={EIGHTBIT_CONNECT_PLATFORM_ICONS}
      iconTileClassName="ninxl-connect-platform-icon-tile"
      iconClassName="ninxl-connect-platform-icon-glyph"
      title={
        <>
          <div className="ninxl-8bit-label ninxl-theme-text-accent mb-3 tracking-[0.2em]">
            CONNECT PLATFORM
          </div>
          <h2
            id="nin-connect-platform-heading"
            className="ninxl-8bit-heading ninxl-theme-text mx-auto max-w-3xl text-lg md:text-xl"
          >
            {copy.heading}
          </h2>
        </>
      }
      subtitle={
        <p className="ninxl-8bit-body mx-auto mt-4 max-w-xl text-sm md:text-base">{copy.subtitle}</p>
      }
      cta={
        <>
          <Link
            href={NIN_CONNECT_PLATFORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ninxl-pixel-btn ninxl-pixel-btn-primary"
          >
            Open Connect
          </Link>
          <Link
            href={NIN_MCP_SERVER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ninxl-pixel-btn"
          >
            MCP plugins
          </Link>
          <Link
            href={NIN_GITHUB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ninxl-pixel-btn"
          >
            <Github className="mr-1.5 inline h-4 w-4" aria-hidden />
            Source
          </Link>
        </>
      }
    />
  )
})
